/**
 * Session Manager - Handles idle timeout and session expiration
 * 
 * Features:
 * - Tracks user activity (mouse, keyboard, clicks, focus)
 * - Auto-logout on idle timeout
 * - Shows warning before session expires
 * - Syncs across browser tabs
 */

import { clearAuthAndRedirect } from './api';

class SessionManager {
  constructor() {
    this.idleTimeoutMinutes = 30; // Default, will be updated from backend
    this.warningBeforeMinutes = 2; // Show warning 2 minutes before logout
    this.idleTimer = null;
    this.warningTimer = null;
    this.lastActivity = Date.now();
    this.isWarningShown = false;
    this.onWarning = null;
    this.onSessionExpired = null;
    this.initialized = false;

    // Activity events to track
    this.activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'focus'
    ];

    // Bind methods
    this.handleActivity = this.handleActivity.bind(this);
    this.handleStorageChange = this.handleStorageChange.bind(this);
    this.checkIdleTimeout = this.checkIdleTimeout.bind(this);
  }

  /**
   * Initialize the session manager
   * @param {Object} config - Configuration object
   * @param {number} config.idleTimeoutMinutes - Idle timeout in minutes
   * @param {Function} config.onWarning - Callback when warning should be shown
   * @param {Function} config.onSessionExpired - Callback when session expires
   */
  init(config = {}) {
    if (this.initialized) {
      this.destroy();
    }

    // Load config from localStorage or use provided/defaults
    const storedConfig = localStorage.getItem('sessionConfig');
    if (storedConfig) {
      try {
        const parsed = JSON.parse(storedConfig);
        this.idleTimeoutMinutes = parsed.session_idle_timeout_minutes || 30;
      } catch (e) {
        this.idleTimeoutMinutes = 30;
      }
    }

    // Override with provided config
    if (config.idleTimeoutMinutes) {
      this.idleTimeoutMinutes = config.idleTimeoutMinutes;
    }

    this.onWarning = config.onWarning || null;
    this.onSessionExpired = config.onSessionExpired || null;

    // Add event listeners for user activity
    this.activityEvents.forEach(event => {
      document.addEventListener(event, this.handleActivity, { passive: true });
    });

    // Listen for storage changes (for multi-tab sync)
    window.addEventListener('storage', this.handleStorageChange);

    // Listen for visibility change
    document.addEventListener('visibilitychange', this.handleActivity);

    // Set initial last activity
    this.updateLastActivity();

    // Start idle check interval
    this.startIdleCheck();

    this.initialized = true;
    console.log(`Session manager initialized with ${this.idleTimeoutMinutes} minute timeout`);
  }

  /**
   * Destroy the session manager and clean up
   */
  destroy() {
    // Remove event listeners
    this.activityEvents.forEach(event => {
      document.removeEventListener(event, this.handleActivity);
    });
    window.removeEventListener('storage', this.handleStorageChange);
    document.removeEventListener('visibilitychange', this.handleActivity);

    // Clear timers
    if (this.idleTimer) {
      clearInterval(this.idleTimer);
      this.idleTimer = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }

    this.initialized = false;
    this.isWarningShown = false;
  }

  /**
   * Handle user activity
   */
  handleActivity() {
    // Don't track activity if not authenticated
    if (!localStorage.getItem('accessToken')) {
      return;
    }

    this.updateLastActivity();

    // Reset warning if shown
    if (this.isWarningShown) {
      this.isWarningShown = false;
    }
  }

  /**
   * Update last activity timestamp
   */
  updateLastActivity() {
    this.lastActivity = Date.now();
    // Store in localStorage for multi-tab sync
    localStorage.setItem('lastActivity', this.lastActivity.toString());
  }

  /**
   * Handle storage changes (for multi-tab sync)
   */
  handleStorageChange(event) {
    if (event.key === 'lastActivity') {
      // Another tab had activity, update our timestamp
      const newActivity = parseInt(event.newValue, 10);
      if (newActivity > this.lastActivity) {
        this.lastActivity = newActivity;
        this.isWarningShown = false;
      }
    } else if (event.key === 'accessToken' && event.newValue === null) {
      // Another tab logged out
      this.handleSessionExpired('You have been logged out from another tab.');
    }
  }

  /**
   * Start the idle check interval
   */
  startIdleCheck() {
    // Check every 10 seconds
    this.idleTimer = setInterval(this.checkIdleTimeout, 10000);
  }

  /**
   * Check if idle timeout has been reached
   */
  checkIdleTimeout() {
    // Don't check if not authenticated
    if (!localStorage.getItem('accessToken')) {
      return;
    }

    // 0 means idle timeout is disabled — rely solely on token expiry
    if (this.idleTimeoutMinutes === 0) {
      return;
    }

    const now = Date.now();
    const idleTime = now - this.lastActivity;
    const idleTimeoutMs = this.idleTimeoutMinutes * 60 * 1000;
    const warningTimeMs = (this.idleTimeoutMinutes - this.warningBeforeMinutes) * 60 * 1000;

    // Check if we should show warning
    if (!this.isWarningShown && idleTime >= warningTimeMs && idleTime < idleTimeoutMs) {
      this.showWarning();
    }

    // Check if session expired
    if (idleTime >= idleTimeoutMs) {
      this.handleSessionExpired('Your session has expired due to inactivity.');
    }
  }

  /**
   * Show session expiry warning
   */
  showWarning() {
    this.isWarningShown = true;
    const remainingMinutes = this.warningBeforeMinutes;

    if (this.onWarning) {
      this.onWarning({
        message: `Your session will expire in ${remainingMinutes} minute${remainingMinutes > 1 ? 's' : ''} due to inactivity.`,
        remainingMinutes
      });
    }
  }

  /**
   * Handle session expiration
   */
  handleSessionExpired(message) {
    this.destroy();

    if (this.onSessionExpired) {
      this.onSessionExpired(message);
    } else {
      clearAuthAndRedirect(message);
    }
  }

  /**
   * Extend session (called when user explicitly wants to stay logged in)
   */
  extendSession() {
    this.updateLastActivity();
    this.isWarningShown = false;
  }

  /**
   * Get remaining session time in milliseconds
   */
  getRemainingTime() {
    const now = Date.now();
    const idleTime = now - this.lastActivity;
    const idleTimeoutMs = this.idleTimeoutMinutes * 60 * 1000;
    return Math.max(0, idleTimeoutMs - idleTime);
  }

  /**
   * Check if session is active
   */
  isSessionActive() {
    return this.getRemainingTime() > 0 && !!localStorage.getItem('accessToken');
  }
}

// Export singleton instance
const sessionManager = new SessionManager();
export default sessionManager;
